import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const About = () => {
  const [ref, inView] = useInView({
    threshold: 0.3,
    triggerOnce: true
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8 }
    }
  };

  return (
    <section id="sanctuary" className="py-20 bg-gradient-to-br from-mist-grey to-white relative overflow-hidden">
      {/* Floating elements */}
      <div className="absolute inset-0 pointer-events-none">
        {[...Array(4)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-64 h-64 rounded-full bg-forest-green/5"
            initial={{ 
              x: Math.random() * window.innerWidth,
              y: Math.random() * window.innerHeight 
            }}
            animate={{
              x: Math.random() * window.innerWidth,
              y: Math.random() * window.innerHeight,
            }}
            transition={{
              duration: 30 + Math.random() * 20,
              repeat: Infinity,
              ease: "linear"
            }}
          />
        ))}
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="grid lg:grid-cols-2 gap-16 items-center"
        >
          {/* Content */}
          <motion.div variants={itemVariants} className="space-y-8">
            <div>
              <motion.h2 
                variants={itemVariants}
                className="text-4xl md:text-5xl font-playfair font-bold text-forest-green mb-6"
              >
                The Thalagaha Experience
              </motion.h2>
              
              <motion.div 
                variants={itemVariants}
                className="w-24 h-1 bg-terracotta-earth mb-8"
              ></motion.div>
            </div>

            <motion.p 
              variants={itemVariants}
              className="text-lg font-lato text-gray-700 leading-relaxed"
            >
              Nestled in the buffer zone of the Sinharaja Forest Reserve, Thalagaha Estate 
              offers an exclusive retreat where privacy meets primal nature. Our villa serves 
              as your private gateway to one of the world's most biodiverse ecosystems.
            </motion.p>

            <motion.p 
              variants={itemVariants}
              className="text-lg font-lato text-gray-700 leading-relaxed"
            >
              The mist rolls in at 4 PM, the call of the hornbill marks the morning, 
              and in between, you'll discover a sanctuary designed for those who seek 
              authentic connection with Sri Lanka's natural and cultural heritage.
            </motion.p>

            <motion.div 
              variants={itemVariants}
              className="grid grid-cols-3 gap-8 pt-8"
            >
              <div className="text-center">
                <div className="text-3xl font-playfair font-bold text-terracotta-earth mb-2">500M</div>
                <div className="text-sm font-lato text-gray-600 uppercase tracking-wide">Years Old Caves</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-playfair font-bold text-terracotta-earth mb-2">121</div>
                <div className="text-sm font-lato text-gray-600 uppercase tracking-wide">Historic Walawwa Rooms</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-playfair font-bold text-terracotta-earth mb-2">0km</div>
                <div className="text-sm font-lato text-gray-600 uppercase tracking-wide">To Heritage Sites</div>
              </div>
            </motion.div>
          </motion.div>

          {/* Image */}
          <motion.div 
            variants={itemVariants}
            className="relative"
          >
            <div className="glass-card p-8 h-96 bg-gradient-to-br from-forest-green/20 to-terracotta-earth/20 flex items-center justify-center">
              <div className="text-center text-forest-green">
                <div className="text-6xl mb-4">🏞️</div>
                <p className="font-lato text-lg">Villa Photography</p>
                <p className="font-lato text-sm opacity-70">Professional imagery showcase</p>
              </div>
            </div>
            
            {/* Floating accent */}
            <motion.div
              animate={{ 
                rotate: 360,
                scale: [1, 1.1, 1]
              }}
              transition={{ 
                rotate: { duration: 20, repeat: Infinity, ease: "linear" },
                scale: { duration: 4, repeat: Infinity, ease: "easeInOut" }
              }}
              className="absolute -top-4 -right-4 w-16 h-16 rounded-full bg-gradient-to-br from-terracotta-earth to-forest-green opacity-20"
            ></motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;